import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { PressableScale } from '@/components/ui/pressable-scale';
import { PublicBrandHeader } from '@/components/public/public-brand-header';
import { PublicBrandFooter } from '@/components/public/public-brand-footer';
import {
  getStoredGuestIdentity,
  getStoredOtpToken,
  useRequestGuestOtp,
  useVerifyGuestOtp,
  type GuestIdentity,
} from '@/hooks/use-public-schedule';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';

const DEFAULT_BRAND = '#FD5001';

/**
 * Convidado vira cliente com conta (claim): o Client provisório criado no
 * agendamento já tem nome e telefone, e o telefone provado basta para logar
 * depois só pelo WhatsApp — e-mail e senha são opcionais, um atalho a mais
 * para quem quer entrar de outro aparelho sem o link mágico. O otpToken
 * guardado no device é a prova de posse do telefone; sem ele, revalidamos
 * por código antes de concluir.
 */
export default function ClaimGuestAccountScreen() {
  const { appointmentId, token, slug, brand } = useLocalSearchParams<{
    appointmentId?: string;
    token?: string;
    slug?: string;
    brand?: string;
  }>();
  const brandColor = typeof brand === 'string' && brand ? brand : DEFAULT_BRAND;

  const { addToast } = useToastStore();
  const claimGuestAccount = useAuthStore((state) => state.claimGuestAccount);

  const [identity, setIdentity] = useState<GuestIdentity | null>(null);
  const [otpToken, setOtpToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [wantsCredentials, setWantsCredentials] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const requestOtp = useRequestGuestOtp(slug);
  const verifyOtp = useVerifyGuestOtp(slug);

  useEffect(() => {
    void (async () => {
      const stored = await getStoredGuestIdentity();
      setIdentity(stored);
      setName(stored?.name ?? '');
      if (stored) {
        setOtpToken(await getStoredOtpToken(stored.phone));
      }
      setLoading(false);
    })();
  }, []);

  const needsOtp = !!identity && !otpToken;

  const sendCode = async () => {
    if (!identity || !slug) return;
    try {
      const { devCode } = await requestOtp.mutateAsync(identity.phone);
      if (devCode) setCode(devCode);
      addToast('Código enviado para o seu WhatsApp.', 'success');
    } catch (err: any) {
      addToast(getApiErrorMessage(err, 'Não foi possível enviar o código.'), 'error');
    }
  };

  const handleSubmit = async () => {
    if (!identity) return;
    // Credenciais são opcionais, mas se o convidado começou a preencher,
    // entram valendo ou nem entram — não faz sentido salvar só metade.
    if (wantsCredentials) {
      if (!email.includes('@') || password.length < 8) {
        addToast('Informe um e-mail válido e senha com 8+ caracteres.', 'error');
        return;
      }
    }

    setSubmitting(true);
    try {
      let proof = otpToken;
      if (!proof) {
        if (code.trim().length !== 6) {
          addToast('Digite o código de 6 dígitos.', 'error');
          return;
        }
        const verified = await verifyOtp.mutateAsync({
          phone: identity.phone,
          code: code.trim(),
        });
        proof = verified.otpToken;
        setOtpToken(proof);
      }

      // Em caso de sucesso o próprio store navega para a área do cliente.
      await claimGuestAccount({
        otpToken: proof,
        email: wantsCredentials ? email.trim() : undefined,
        password: wantsCredentials ? password : undefined,
        name: name.trim() || undefined,
      });
    } catch (err: any) {
      addToast(getApiErrorMessage(err, 'Não foi possível criar sua conta.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <View className="flex-1 items-center justify-center bg-neutral-50">
        <ActivityIndicator size="large" color={brandColor} />
      </View>
    );
  }

  // Sem identidade guardada não há telefone para provar: o caminho é o login.
  if (!identity) {
    return (
      <View className="flex-1 items-center justify-center gap-3 bg-neutral-50 p-8">
        <Ionicons className="text-neutral-400" name="person-circle-outline" size={48} />
        <ThemedText className="text-center font-bold text-xl">
          Não achamos seu agendamento aqui
        </ThemedText>
        <ThemedText className="text-center text-neutral-500">
          Abra o link que enviamos no seu WhatsApp neste dispositivo, ou entre com a conta que você
          já tem.
        </ThemedText>
        <PressableScale
          onPress={() => router.replace('/(auth)/login' as never)}
          className="mt-2 rounded-full px-6 py-3"
          style={{ backgroundColor: brandColor }}
        >
          <ThemedText className="font-bold text-white">Entrar</ThemedText>
        </PressableScale>
      </View>
    );
  }

  const busy = submitting || requestOtp.isPending || verifyOtp.isPending;

  return (
    <View className="flex-1 bg-neutral-50">
      <PublicBrandHeader />
      <KeyboardAwareScrollView
        bottomOffset={KEYBOARD_BOTTOM_OFFSET}
        className="flex-1 bg-neutral-50"
        contentContainerStyle={{ flexGrow: 1, padding: 20, gap: 20 }}
        keyboardShouldPersistTaps="handled"
      >
        <View className="gap-2">
          <ThemedText className="font-bold text-2xl">Criar minha conta</ThemedText>
          <ThemedText className="text-sm leading-5 text-neutral-500">
            Seu WhatsApp já é a sua identidade aqui — é só confirmar para acompanhar seus
            agendamentos sempre que voltar.
          </ThemedText>
        </View>

        <View className="gap-3 rounded-3xl bg-card p-5" style={{ borderCurve: 'continuous' }}>
          <View className="flex-row items-center gap-2">
            <Ionicons className="text-success-600" name="logo-whatsapp" size={16} />
            <ThemedText className="font-semibold text-sm text-neutral-600">
              {identity.phone}
            </ThemedText>
            {!needsOtp && (
              <View className="rounded-full bg-green-50 px-2 py-0.5">
                <ThemedText className="font-bold text-[11px] text-green-700">verificado</ThemedText>
              </View>
            )}
          </View>

          <Input
            placeholder="Seu nome"
            value={name}
            onChangeText={setName}
            autoCapitalize="words"
            editable={!busy}
          />

          {wantsCredentials ? (
            <>
              <Input
                placeholder="E-mail"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                editable={!busy}
              />
              <Input
                placeholder="Senha (mínimo 8 caracteres)"
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                autoComplete="new-password"
                editable={!busy}
              />
              <Pressable
                disabled={busy}
                onPress={() => {
                  setWantsCredentials(false);
                  setEmail('');
                  setPassword('');
                }}
                className="items-center py-1"
              >
                <ThemedText className="font-semibold text-sm text-neutral-500">
                  Não precisa, só o WhatsApp já resolve
                </ThemedText>
              </Pressable>
            </>
          ) : (
            <Pressable
              disabled={busy}
              onPress={() => setWantsCredentials(true)}
              className="flex-row items-center gap-1.5 py-1"
            >
              <Ionicons className="text-neutral-500" name="add-circle-outline" size={16} />
              <ThemedText className="font-semibold text-sm text-neutral-500">
                Quero entrar também com e-mail e senha
              </ThemedText>
            </Pressable>
          )}

          {needsOtp && (
            <View className="gap-2">
              <ThemedText className="text-sm text-neutral-500">
                Para sua segurança, confirme o código de 6 dígitos que enviamos no seu WhatsApp.
              </ThemedText>
              <Input
                placeholder="000000"
                value={code}
                onChangeText={setCode}
                keyboardType="number-pad"
                maxLength={6}
                className="text-center text-2xl tracking-[8px]"
                editable={!busy}
              />
              <Pressable disabled={busy || !slug} onPress={sendCode} className="items-center py-1">
                <ThemedText className="font-semibold text-sm text-neutral-500">
                  Enviar código
                </ThemedText>
              </Pressable>
            </View>
          )}

          <PressableScale
            haptic
            disabled={busy}
            onPress={handleSubmit}
            className="mt-1 w-full flex-row items-center justify-center gap-2 rounded-full py-4"
            style={{ backgroundColor: brandColor, borderCurve: 'continuous' }}
          >
            {busy ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <ThemedText className="font-bold text-base text-white">Criar conta</ThemedText>
            )}
          </PressableScale>
        </View>

        <Pressable
          onPress={() =>
            // Recorrência não tem um agendamento só: volta para a lista.
            router.replace(
              (appointmentId
                ? {
                    pathname: '/agendamento/[appointmentId]',
                    params: { appointmentId, token: token ?? '' },
                  }
                : '/agendamento') as never,
            )
          }
          className="items-center py-2"
        >
          <ThemedText className="font-semibold text-sm text-neutral-500">
            {appointmentId ? 'Agora não, ver meu agendamento' : 'Agora não, ver meus agendamentos'}
          </ThemedText>
        </Pressable>

        <View className="flex-1" />
        <View style={{ marginHorizontal: -20, marginTop: 24 }}>
          <PublicBrandFooter />
        </View>
      </KeyboardAwareScrollView>
    </View>
  );
}
